import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiStar, FiChevronDown, FiShield, FiBattery, FiFeather, FiCheck, FiArrowRight, FiPlay } = FiIcons;

function App() {
  const [openFaq, setOpenFaq] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const testimonials = [
    {
      name: "Rahul Sharma",
      role: "Daily Biker",
      text: "Finally, I can ride through Delhi traffic without that burning sensation in my throat. The necklace is so light, I forget I'm wearing it!",
      rating: 5
    },
    {
      name: "Priya Patel",
      role: "Working Mother",
      text: "As a mom commuting to work daily, this gives me peace of mind. My morning cough is completely gone after just 2 weeks of use.",
      rating: 5
    },
    {
      name: "Arjun Kumar",
      role: "IT Professional",
      text: "Sleek design that goes with my office attire. Colleagues keep asking where I got it. The battery easily lasts my entire workday.",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "How do I charge the device?",
      answer: "Simply use the included magnetic USB charger. It takes just 2 hours for a full charge that lasts 24+ hours."
    },
    {
      question: "Does it make any noise?",
      answer: "Absolutely silent. Our advanced micro-fan technology operates at whisper-quiet levels below 20dB."
    },
    {
      question: "What's your return policy?",
      answer: "30-day money-back guarantee. If you're not completely satisfied, return it for a full refund, no questions asked."
    },
    {
      question: "Is it safe to wear all day?",
      answer: "Yes! Made with medical-grade materials and extensively tested for 24/7 wear. Hypoallergenic and skin-safe."
    },
    {
      question: "How effective is it against PM2.5?",
      answer: "Our technology creates a 95% reduction zone for PM2.5 particles within a 30cm radius around your breathing area."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky CTA for Mobile */}
      <motion.div 
        className="fixed bottom-0 left-0 right-0 bg-teal-600 text-white p-4 z-50 md:hidden"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ delay: 1 }}
      >
        <button className="w-full bg-white text-teal-600 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors">
          Buy Now for ₹9,999
        </button>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            className="space-y-8"
            initial="initial"
            animate="animate"
            variants={staggerChildren}
          >
            <motion.div variants={fadeInUp} className="space-y-4">
              <div className="flex items-center space-x-2 text-teal-600 font-medium">
                <SafeIcon icon={FiStar} className="w-5 h-5" />
                <span>Rated 4.9/5 by 10,000+ Indian Commuters</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Your Personal <span className="text-teal-600">Clean Air Zone.</span> Anywhere.
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                A lightweight necklace that reduces exposure to PM2.5, smoke, and dust for 24 hours on a single charge.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <button className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors flex items-center justify-center space-x-2">
                <span>Buy Now for ₹9,999</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-teal-600 hover:text-teal-600 transition-colors flex items-center justify-center space-x-2">
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
                <span>Watch Demo</span>
              </button>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center space-x-8 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-teal-600" />
                <span>30-Day Guarantee</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-teal-600" />
                <span>Free Shipping</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2088&q=80"
                alt="Person wearing air purification necklace"
                className="rounded-2xl shadow-2xl"
              />
              
              {/* Pulsing Animation */}
              <motion.div
                className="absolute inset-0 rounded-2xl"
                animate={{
                  boxShadow: [
                    "0 0 0 0 rgba(20, 184, 166, 0.4)",
                    "0 0 0 20px rgba(20, 184, 166, 0)",
                    "0 0 0 0 rgba(20, 184, 166, 0)"
                  ]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Engineered for Indian Cities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every concern addressed. Every objection solved. Pure engineering meets daily comfort.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.div variants={fadeInUp} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <SafeIcon icon={FiFeather} className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Forgot you're wearing it</h3>
              <p className="text-gray-600 mb-4">
                At just 45 grams, lighter than most smartphones. Ergonomic design tested on 1000+ users for all-day comfort.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Addresses: "Is it heavy?"
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <SafeIcon icon={FiBattery} className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Outlasts your longest day</h3>
              <p className="text-gray-600 mb-4">
                24+ hour battery life with fast 2-hour charging. Smart power management adjusts to pollution levels automatically.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Addresses: "Will the battery die?"
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <SafeIcon icon={FiShield} className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traffic-proof protection</h3>
              <p className="text-gray-600 mb-4">
                Creates a 95% clean air zone even in heavy smog. Tested in Delhi, Mumbai, and Bangalore traffic conditions.
              </p>
              <div className="text-sm text-teal-600 font-medium">
                Addresses: "Does it really work outside?"
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof - Wall of Love */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Wall of Love
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from real commuters across India
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about your clean air companion
            </p>
          </motion.div>

          <motion.div 
            className="space-y-4"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-lg shadow-sm"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <motion.div
                    animate={{ rotate: openFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SafeIcon icon={FiChevronDown} className="w-5 h-5 text-gray-500" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 text-gray-600">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Founder's Note */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div 
            className="text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-12">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                alt="Founder"
                className="w-24 h-24 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-6">A Personal Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed italic mb-6">
                "I lived in Delhi and hated the daily cough that came with my morning commute. Every mask felt suffocating, every air purifier was too bulky for travel. I couldn't find a solution I actually wanted to use every day. So I built this necklace to reclaim our right to breathe clean air, wherever we are."
              </p>
              <div className="text-gray-900 font-semibold">
                — Ankit Mehta, Founder & CEO
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Don't let pollution dictate your health.
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of Indians who've taken control of their breathing. Limited time offer.
            </p>
            <div className="space-y-4">
              <button className="bg-white text-teal-600 px-12 py-4 rounded-lg font-bold text-xl hover:bg-gray-100 transition-colors inline-flex items-center space-x-3">
                <span>Get 50% Off - Limited Time</span>
                <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
              </button>
              <div className="text-sm opacity-75">
                ₹19,999 → ₹9,999 | Free shipping across India | 30-day guarantee
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-2">AirGuard</h3>
            <p className="text-gray-400">Breathe freely, live fully</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Contact</h4>
              <p className="text-gray-400">support@airguard.in</p>
              <p className="text-gray-400">1800-123-4567</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Shipping</h4>
              <p className="text-gray-400">Free across India</p>
              <p className="text-gray-400">2-3 business days</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Warranty</h4>
              <p className="text-gray-400">2 years full coverage</p>
              <p className="text-gray-400">30-day returns</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-gray-400 text-sm">
            © 2024 AirGuard. Made in India for Indian air.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;